import React, { useState } from "react";

import {
  useCreateAdminFarmOrganicFertilizerProduction,
  useDeleteAdminFarmOrganicFertilizerProduction,
  useGetAdminFarmOrganicFertilizerProduction,
  useGetPracticeOrganicFertilizers,
  useUpdateAdminFarmOrganicFertilizerProduction,
} from "utils";

import { ANIMAL_BASED_FERTILIZER_SLUGS } from "consts";

import { FarmOrganicFertilizerProductionEntry } from "models";

import { Button, Collapse, Flex, Icon, Tag, Typography } from "components";

import { AdminOrganicFertilizerSourcesDrawer } from "pages/admin/verifications/components/misc/AdminOrganicFertilizerSourcesDrawer";

type Props = {
  farmId: string;
  selfProducedFertilizersByYear: Record<number, string[]>;
};

type EditingTarget = { fertilizerId: string; year: number } | null;

function getSourceLines(records: FarmOrganicFertilizerProductionEntry[], isAnimalBased: boolean): string[] {
  const ownRecord = records.find((r) => r.sourceType === "own_production");
  const coopRecord = records.find((r) => r.sourceType === "cooperation");
  const lines: string[] = [];

  if (ownRecord) {
    if (isAnimalBased) {
      lines.push(`Own production · ${ownRecord.livestockCount ?? "—"} livestock`);
    } else {
      const count = ownRecord.ownProductionItems.length;
      lines.push(`Own production · ${count} item${count !== 1 ? "s" : ""}`);
    }
  }

  if (coopRecord) {
    const count = coopRecord.cooperationItems.length;
    lines.push(`Farm cooperation · ${count} farm${count !== 1 ? "s" : ""}`);
  }

  return lines.length > 0 ? lines : ["No data"];
}

export const OrganicFertilizerSourcesPanel = ({ farmId, selfProducedFertilizersByYear }: Props) => {
  const [editing, setEditing] = useState<EditingTarget>(null);
  const [open, setOpen] = useState(false);

  const { data: productions, isLoading } = useGetAdminFarmOrganicFertilizerProduction({ farmId });
  const { data: organicFertilizers } = useGetPracticeOrganicFertilizers({});

  const { mutateAsync: createProduction } = useCreateAdminFarmOrganicFertilizerProduction({ farmId });
  const { mutateAsync: updateProduction } = useUpdateAdminFarmOrganicFertilizerProduction({ farmId });
  const { mutateAsync: deleteProduction } = useDeleteAdminFarmOrganicFertilizerProduction({ farmId });

  if (isLoading) return null;

  // Group productions by year, then by fertilizerId
  const byYear = (productions ?? []).reduce<Record<number, Record<string, FarmOrganicFertilizerProductionEntry[]>>>((acc, prod) => {
    if (!prod.fertilizerId || !prod.year) return acc;
    if (!acc[prod.year]) acc[prod.year] = {};
    if (!acc[prod.year][prod.fertilizerId]) acc[prod.year][prod.fertilizerId] = [];
    acc[prod.year][prod.fertilizerId].push(prod);
    return acc;
  }, {});

  // All years from both DB productions and field practices (self-produced)
  const allYears = [...new Set([...Object.keys(byYear).map(Number), ...Object.keys(selfProducedFertilizersByYear).map(Number)])]
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  if (!allYears.length) return null;

  const editingRecords = editing ? byYear[editing.year]?.[editing.fertilizerId] ?? [] : [];
  const editingFertilizer = editing ? organicFertilizers?.find((f) => f.id === editing.fertilizerId) : undefined;

  const columnsContent = (
    <div className="organic-fertilizer-sources-columns">
      {allYears.map((year) => {
        const allFertilizerIds = [...new Set([...Object.keys(byYear[year] ?? {}), ...(selfProducedFertilizersByYear[year] ?? [])])];

        return (
          <div key={year} className="organic-fertilizer-sources-column">
            <Tag colour="grey" text={`${year}`} />

            <div className="column-entries">
              {allFertilizerIds.map((fertilizerId) => {
                const fertilizer = organicFertilizers?.find((f) => f.id === fertilizerId);
                const records = byYear[year]?.[fertilizerId] ?? [];
                const hasRecords = records.length > 0;
                const isAnimalBased = ANIMAL_BASED_FERTILIZER_SLUGS.has(fertilizer?.translationSlug ?? "");
                const lines = hasRecords ? getSourceLines(records, isAnimalBased) : null;

                return (
                  <Flex key={fertilizerId} align="center" justify="space-between" gap="0.75rem" className="column-entry">
                    <Flex vertical gap="0.125rem">
                      <Typography variant="paragraph-s" bold className="margin-0">
                        {fertilizer?.name ?? fertilizerId}
                      </Typography>
                      {hasRecords ? (
                        <>
                          {lines!.map((line) => (
                            <Typography key={line} variant="paragraph-s" color="grey-600" className="margin-0">
                              {line}
                            </Typography>
                          ))}
                          <Tag colour="green" text="Complete" />
                        </>
                      ) : (
                        <Tag colour="red" text="Incomplete" />
                      )}
                    </Flex>
                    <Button type="text" size="small" onClick={() => setEditing({ fertilizerId, year })}>
                      Edit
                    </Button>
                  </Flex>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <Collapse
        className="organic-fertilizer-sources-panel"
        activeKey={open ? ["sources"] : []}
        onChange={() => setOpen((v) => !v)}
        items={[
          {
            key: "sources",
            showArrow: false,
            label: (
              <Flex align="center" gap="0.375rem">
                <Typography variant="h3" className="margin-0">
                  Organic fertiliser sources
                </Typography>
                <Icon iconName={open ? "tabler/chevron-up" : "tabler/chevron-down"} size="m-light" />
              </Flex>
            ),
            children: columnsContent,
          },
        ]}
      />

      {editing && (
        <AdminOrganicFertilizerSourcesDrawer
          visible
          farmId={farmId}
          year={editing.year}
          fertilizerId={editing.fertilizerId}
          fertilizerName={editingFertilizer?.name ?? editing.fertilizerId}
          fertilizerSlug={editingFertilizer?.translationSlug ?? ""}
          existingRecords={editingRecords}
          onClose={() => setEditing(null)}
          createProduction={createProduction}
          updateProduction={updateProduction}
          deleteProduction={deleteProduction}
        />
      )}
    </>
  );
};
